import {
  type AdminUserRow,
  type AdminUsersSortBy,
  type UserRole,
  type UserStatus,
} from "@nexys/user-management-client";
import {
  AlertCircle,
  ArrowUpDown,
  Eye,
  KeyRound,
  ListFilter,
  MoreHorizontal,
  Power,
  RefreshCcw,
  Search,
  UserCheck,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative, initialsOf } from "../../lib/utils.js";
import { EmptyState } from "../primitives/empty-state.js";
import { StatusBadge, UserRoleBadge } from "../roles/role-badge.js";
import { UserRoleSelect } from "../roles/role-select.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Avatar, AvatarFallback } from "../ui/avatar.js";
import { Button } from "../ui/button.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.js";
import { Input } from "../ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { Skeleton } from "../ui/skeleton.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.js";
import { Badge } from "../ui/badge.js";
import { UserCreateDialog } from "./user-create-dialog.js";

const NEXT_STATUS: Record<UserStatus, UserStatus> = {
  active: "inactive",
  inactive: "active",
  pending: "active",
};

/** "<sortBy>-<order>" values the sort select offers, A–Z first. */
const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "name-asc", label: "Name A–Z" },
  { value: "name-desc", label: "Name Z–A" },
  { value: "email-asc", label: "Email A–Z" },
  { value: "email-desc", label: "Email Z–A" },
  { value: "createdAt-desc", label: "Newest first" },
  { value: "createdAt-asc", label: "Oldest first" },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "inactive", label: "Inactive" },
];

export interface UsersTableProps {
  /** Server-side filters. */
  status?: UserStatus;
  /** Visible role select — set to false in read-only contexts. */
  allowRoleChange?: boolean;
  /** Show the "New user" button (superadmin only on the server). */
  allowCreate?: boolean;
  /**
   * Called when a row is opened — wire this to navigation towards the
   * user detail page (edit, status, roles, auth methods live there).
   */
  onOpenUser?: (user: AdminUserRow) => void;
  /** When true, includes soft-deleted users (superadmin only). */
  includeDeleted?: boolean;
  pageSize?: number;
}

export const UsersTable = ({
  status,
  allowRoleChange = true,
  allowCreate = false,
  onOpenUser,
  includeDeleted = false,
  pageSize = 50,
}: UsersTableProps) => {
  const client = useUmClient();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("name-asc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // A fixed `status` prop wins over the toolbar filter.
  const filterActive = status === undefined && statusFilter !== "all";
  const effectiveStatus =
    status ?? (filterActive ? (statusFilter as UserStatus) : undefined);
  const [sortBy, order] = sort.split("-") as [AdminUsersSortBy, "asc" | "desc"];

  const { data, error, loading, refresh } = useCall("adminListUsers", {
    status: effectiveStatus,
    includeDeleted,
    limit: pageSize,
    offset: 0,
    sortBy,
    order,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.name.toLowerCase().includes(q) ||
        u.id.toLowerCase().includes(q)
    );
  }, [data, query]);

  const withBusy = async <O, E>(
    u: AdminUserRow,
    op: () => Promise<{ ok: true; data: O } | { ok: false; error: E }>
  ) => {
    setBusyId(u.id);
    setActionError(null);
    const res = await op();
    setBusyId(null);
    if (res.ok) {
      await refresh();
    } else {
      setActionError(errorMessage(res.error as never, "Action failed."));
    }
  };

  const setStatus = (u: AdminUserRow, next: UserStatus) =>
    withBusy(u, () => client.adminSetUserStatus({ userId: u.id, status: next }));
  const setRole = (u: AdminUserRow, role: UserRole) =>
    withBusy(u, () => client.adminSetUserRole({ userId: u.id, role }));
  const revokeTokens = (u: AdminUserRow) =>
    withBusy(u, () => client.adminRevokeUserTokens({ userId: u.id }));

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative min-w-[14rem] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email or ID…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {status === undefined ? (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="h-8 w-[9.5rem]"
                aria-label="Filter by status"
              >
                <ListFilter className="size-3.5 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-8 w-[9.5rem]" aria-label="Sort by">
              <ArrowUpDown className="size-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCcw />
            Refresh
          </Button>
          {allowCreate ? <UserCreateDialog onCreated={() => void refresh()} /> : null}
        </div>
      </div>

      {actionError ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{errorMessage(error)}</AlertDescription>
        </Alert>
      ) : null}

      <div className="overflow-hidden rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sign-in</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Skeleton className="size-9 rounded-full" />
                      <div className="grid gap-1.5">
                        <Skeleton className="h-3.5 w-32" />
                        <Skeleton className="h-3 w-44" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-3.5 w-20" /></TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="ml-auto h-8 w-8 rounded-md" />
                  </TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="p-0">
                  <EmptyState
                    icon={Users}
                    title={query || filterActive ? "No matches" : "No users yet"}
                    description={
                      query || filterActive
                        ? "Try a different search term or filter."
                        : "Invite someone to get started."
                    }
                    className="border-0 rounded-none bg-transparent"
                  />
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((u) => (
                <TableRow key={u.id} data-busy={busyId === u.id || undefined}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback>
                          {initialsOf(u.name, u.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid leading-tight">
                        {onOpenUser ? (
                          <button
                            type="button"
                            onClick={() => onOpenUser(u)}
                            className="w-fit text-left text-sm font-medium hover:underline"
                          >
                            {u.name || u.email}
                          </button>
                        ) : (
                          <span className="text-sm font-medium">
                            {u.name || u.email}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">{u.email}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {allowRoleChange ? (
                      <UserRoleSelect
                        value={u.role}
                        onChange={(next) => setRole(u, next)}
                        disabled={busyId === u.id}
                      />
                    ) : (
                      <UserRoleBadge role={u.role} />
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={u.status} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(u.providers ?? []).map((p) => (
                        <Badge key={p} variant="outline" className="capitalize">
                          {p === "credential" ? "password" : p}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell title={u.createdAt} className="text-sm text-muted-foreground">
                    {formatRelative(u.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal />
                          <span className="sr-only">Actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Manage user</DropdownMenuLabel>
                        {onOpenUser ? (
                          <DropdownMenuItem onSelect={() => onOpenUser(u)}>
                            <Eye /> View details
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem
                          onSelect={() => setStatus(u, NEXT_STATUS[u.status as UserStatus] ?? "active")}
                        >
                          {u.status === "active" ? (
                            <>
                              <Power /> Deactivate
                            </>
                          ) : (
                            <>
                              <UserCheck /> Activate
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => revokeTokens(u)}>
                          <KeyRound /> Revoke all sessions
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data && data.total > filtered.length && (
        <p className="text-right text-xs text-muted-foreground">
          Showing {filtered.length} of {data.total}
        </p>
      )}
    </div>
  );
};
